import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { TagChip } from 'features/result-content/TagChip';
import { buildTagPayload, matchesTagSearch, sanitizeText } from 'features/result-content/resultContent';
import { Button } from 'shared/components/Button';
import { Badge, EmptyState, ErrorState, Skeleton } from 'shared/components/Feedback';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { BilingualField, SwitchField, TextField } from 'shared/forms/Fields';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

const emptyForm = {
  slug: '',
  name_ar: '',
  name_en: '',
  description_ar: '',
  description_en: '',
  color: '',
  is_active: true,
};

export function TagsPanel({ t, locale, canManage, canView }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(Boolean(showForm && dirty));

  const query = useQuery({
    queryKey: queryKeys.tags,
    enabled: canView,
    queryFn: ({ signal }) => assessmentApi.listTags({ signal }),
  });

  const items = useMemo(() => {
    return (query.data?.items || []).filter((tag) => {
      const matchesStatus = status === 'all'
        || (status === 'active' ? tag.is_active && !tag.is_archived : status === 'inactive' ? !tag.is_active : true);
      return matchesStatus && matchesTagSearch(tag, search);
    });
  }, [query.data, search, status]);

  useEffect(() => {
    if (!editingId || !query.data?.items) {
      return;
    }
    const tag = query.data.items.find((item) => String(item.id) === String(editingId));
    if (!tag) {
      return;
    }
    setForm({
      slug: tag.slug || '',
      name_ar: tag.name_ar || '',
      name_en: tag.name_en || '',
      description_ar: tag.description_ar || '',
      description_en: tag.description_en || '',
      color: tag.color || '',
      is_active: Boolean(tag.is_active),
    });
    setDirty(false);
    setErrors({});
  }, [editingId, query.data]);

  const invalidateLibraries = async (createdTag) => {
    if (createdTag) {
      queryClient.setQueryData(queryKeys.tags, (current) => {
        const itemsList = current?.items || [];
        if (itemsList.some((item) => Number(item.id) === Number(createdTag.id))) {
          return current;
        }
        return { ...(current || {}), items: [createdTag, ...itemsList] };
      });
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.tags });
    await queryClient.invalidateQueries({ queryKey: ['assessment', 'personal-summaries'] });
    await queryClient.invalidateQueries({ queryKey: ['assessment', 'result-items'] });
  };

  const save = useMutation({
    mutationFn: (body) => (editingId ? assessmentApi.updateTag(editingId, body) : assessmentApi.createTag(body)),
    onSuccess: async (data) => {
      setDirty(false);
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      await invalidateLibraries(editingId ? null : data);
      toast.success(editingId ? t('status.updated') : t('status.created'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) setErrors(mapFieldErrors(error.errors));
      toast.error(humanizeApiError(error, t));
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }) => assessmentApi.setTagActivation(id, isActive),
    onSuccess: async () => {
      await invalidateLibraries();
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const removal = useMutation({
    mutationFn: (id) => assessmentApi.deleteTag(id),
    onSuccess: async () => {
      await invalidateLibraries();
      toast.success(t('status.deleted'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const updateField = (key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;

  return (
    <div>
      <div className="toolbar wrap">
        <TextField id="tag-search" label={t('actions.search')} value={search} onChange={setSearch} />
        <label className="field" htmlFor="tag-status">
          <span className="field-label">{t('actions.filter')}</span>
          <select id="tag-status" className="input" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="all">{t('filters.all')}</option>
            <option value="active">{t('filters.active')}</option>
            <option value="inactive">{t('filters.inactive')}</option>
          </select>
        </label>
        {canManage ? (
          <Button
            onClick={() => {
              setEditingId(null);
              setForm(emptyForm);
              setErrors({});
              setShowForm(true);
              setDirty(false);
            }}
          >
            {t('tags.create')}
          </Button>
        ) : null}
      </div>
      {showForm && canManage ? (
        <form
          className="card stack-form"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(buildTagPayload(form));
          }}
        >
          <h2>{editingId ? t('actions.edit') : t('tags.create')}</h2>
          <BilingualField
            id="tag-name"
            labelAr={t('tags.nameAr')}
            labelEn={t('tags.nameEn')}
            valueAr={form.name_ar}
            valueEn={form.name_en}
            onChangeAr={(value) => updateField('name_ar', value)}
            onChangeEn={(value) => updateField('name_en', value)}
            errorAr={errors.name_ar?.[0]}
            errorEn={errors.name_en?.[0]}
          />
          <TextField
            id="tag-slug"
            label={t('tags.slug')}
            value={form.slug}
            onChange={(value) => updateField('slug', value)}
            error={errors.slug?.[0]}
            required
          />
          <BilingualField
            id="tag-description"
            labelAr={t('tags.descriptionAr')}
            labelEn={t('tags.descriptionEn')}
            valueAr={form.description_ar}
            valueEn={form.description_en}
            onChangeAr={(value) => updateField('description_ar', value)}
            onChangeEn={(value) => updateField('description_en', value)}
            errorAr={errors.description_ar?.[0]}
            errorEn={errors.description_en?.[0]}
            multiline
            required={false}
          />
          <TextField
            id="tag-color"
            label={t('tags.color')}
            value={form.color}
            onChange={(value) => updateField('color', value)}
            error={errors.color?.[0]}
            hint={t('tags.colorHint')}
          />
          <SwitchField id="tag-active" label={t('patterns.active')} checked={form.is_active} onChange={(value) => updateField('is_active', value)} />
          <div className="toolbar">
            <Button type="submit" loading={save.isPending} disabled={save.isPending}>{t('actions.save')}</Button>
            <Button
              tone="ghost"
              onClick={() => {
                setShowForm(false);
                setDirty(false);
              }}
            >
              {t('actions.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
      {items.length === 0 ? <EmptyState title={t('empty.tags')} /> : (
        <div className="card-list">
          {items.map((tag) => (
            <article key={tag.id} className="card">
              <div className="toolbar wrap">
                <TagChip tag={tag} locale={locale} t={t} />
                <Badge tone={tag.is_active ? 'success' : 'neutral'}>{tag.is_active ? t('filters.active') : t('filters.inactive')}</Badge>
                {tag.is_archived ? <Badge tone="warning">{t('resultContent.archivedTag')}</Badge> : null}
              </div>
              <p className="muted">{tag.slug}</p>
              {sanitizeText(locale === 'ar' ? tag.description_ar : tag.description_en) ? (
                <p>{sanitizeText(locale === 'ar' ? tag.description_ar : tag.description_en)}</p>
              ) : null}
              {tag.usage_count != null ? <p className="muted">{t('resultContent.usageCount', { count: tag.usage_count })}</p> : null}
              {canManage ? (
                <div className="toolbar">
                  <Button
                    tone="ghost"
                    onClick={() => {
                      setEditingId(tag.id);
                      setShowForm(true);
                    }}
                  >
                    {t('actions.edit')}
                  </Button>
                  <Button
                    tone="ghost"
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate({ id: tag.id, isActive: !tag.is_active })}
                  >
                    {tag.is_active ? t('actions.deactivate') : t('actions.activate')}
                  </Button>
                  <Button
                    tone="danger"
                    disabled={removal.isPending}
                    onClick={async () => {
                      if (await confirm({ body: t('confirm.archiveTag'), confirmLabel: t('actions.archive') })) {
                        removal.mutate(tag.id);
                      }
                    }}
                  >
                    {t('actions.archive')}
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
